import React, { useEffect, useMemo, useState } from "react";
import { describeRoute, sameRoute } from "./shipmentRoute";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import { Tooltip, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InboxIcon from "@mui/icons-material/Inbox";
import LinkIcon from "@mui/icons-material/Link";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDProgress from "components/MDProgress";
import { useNavigate } from "react-router-dom";
import DashboardNavbar from "../../../../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../../../../examples/LayoutContainers/DashboardLayout";
import Footer from "../../../../../examples/Footer";
import { Pill as TrackingPill } from "./oceanStatus/TrackingDrawer";
import { TONE as TRACKING_TONE, arrivalHint as trackingArrivalHint, rowState as trackingRowState } from "./oceanStatus/constants";
import { appConfirm, appChoose } from "components/AppDialog";
import { RemoveRowButton } from "components/RowActions";

import { ListLink, CrmRowActions, ListIconButton, ListStatus, ListText, CrmPage, CrmPageHeader, CrmActionButton, CrmNotice, CrmTableCard, LoadTypeTabs } from "../crm/shared/CrmUi";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import DirectionsBoatOutlined from "@mui/icons-material/DirectionsBoatOutlined";
function OceanShipmentTable() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState({ fcl: [], lcl: [] });
    const [selectedRowsLCL, setSelectedRowsLCL] = useState([]);
    const [selectedRowsFCL, setSelectedRowsFCL] = useState([]);
    const [notification, setNotification] = useState({ value: false, text: "" });
    const [selectedPlannedRouteCountries, setSelectedPlannedRouteCountries] = useState([]);
    const [tabValue, setTabValue] = useState(0);

    const navigate = useNavigate();
    const MAX_CBM = 90;

    const selectedRowsLCLMemo = useMemo(() => {
        const map = new Map();
        (selectedRowsLCL || []).forEach((s) => map.set(String(s.id), s));
        return Array.from(map.values());
    }, [selectedRowsLCL]);

    const selectedRowsFCLMemo = useMemo(() => {
        const map = new Map();
        (selectedRowsFCL || []).forEach((s) => map.set(String(s.id), s));
        return Array.from(map.values());
    }, [selectedRowsFCL]);

    const selectedTotalCbmLCL = useMemo(
        () =>
            selectedRowsLCLMemo.reduce(
                (sum, s) => sum + Number(s.attributes?.totals?.totalCbm || 0),
                0
            ),
        [selectedRowsLCLMemo]
    );

    const showNotification = (text) => {
        setNotification({ value: true, text });
        setTimeout(() => setNotification({ value: false, text: "" }), 5000);
    };

    const renderText = (value, fallback = "-") => {
        if (value === null || value === undefined || value === "") return fallback;

        if (
            typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean"
        ) {
            return String(value);
        }

        if (Array.isArray(value)) {
            const text = value
                .map((item) => renderText(item, ""))
                .filter(Boolean)
                .join(", ");

            return text || fallback;
        }

        if (typeof value === "object") {
            return (
                value.name ||
                value.label ||
                value.companyName ||
                value.title ||
                value.value ||
                value.id ||
                value._id ||
                fallback
            );
        }

        return fallback;
    };

    const getBlType = (shipment) => {
        const attr = shipment?.attributes || {};

        if (
            attr.billOfLadingType === "DIRECT_BL" ||
            attr.isDirectBL ||
            attr.sourceType === "DBL" ||
            attr.sourceType === "DMBL"
        ) {
            return "DBL";
        }

        return "HBL";
    };

    const isDirectBLShipment = (shipment) => getBlType(shipment) === "DBL";

    const formatNumber = (value) => {
        const n = Number(value || 0);
        return Number.isFinite(n) ? n.toFixed(4) : "0.0000";
    };

    const formatMoney = (amount, currency = "") => {
        const n = Number(amount || 0);

        return `${Number.isFinite(n) ? n.toFixed(2) : "0.00"}${
            currency ? ` ${renderText(currency, "")}` : ""
        }`;
    };

    const getCustomerText = (shipment) => {
        const customer = renderText(shipment?.attributes?.customer, "No Customer Data");

        return customer.length > 38 ? `${customer.substring(0, 36)}...` : customer;
    };

    const chipSx = {
        fontWeight: 700,
        borderRadius: "999px",
        minWidth: 70,
        height: 28,
    };

    const renderBlTypeChip = (type) => (
        <ListStatus label={type} tone={type === "DBL" ? "amber" : "blue"} />
    );

    const renderTransportChip = (type) => (
        <ListStatus label={type || "-"} tone={type?.includes("FCL") ? "green" : "purple"} />
    );

    const renderVolumeChip = (value) => (
        <ListText primary={`${formatNumber(value)} CBM`} strong />
    );

    const renderMoneyChip = (value) => (
        <ListText primary={value} strong />
    );

    const fetchOceanShipments = async () => {
        try {
            setLoading(true);
            const response = await CrudService.getOceanShipments();
            setData(response?.data || []);
        } catch (error) {
            console.error("Error fetching ocean shipments:", error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    // Ocean Shipment Status: carrier tracking per shipment (badge column).
    const [trackings, setTrackings] = useState([]);
    const fetchTrackings = async () => {
        try {
            const response = await CrudService.getOceanTrackings();
            setTrackings((response?.data || []).map((item) => ({ id: item.id, ...item.attributes })));
        } catch (error) {
            // The shipment list works without tracking data.
            setTrackings([]);
        }
    };

    useEffect(() => {
        fetchOceanShipments();
        fetchTrackings();
    }, []);

    // Linked tracking first, then by container; an ongoing journey beats a completed one.
    const trackingFor = (shipment) => {
        const attr = shipment.attributes || {};
        const containers = new Set();
        (attr.cargoDetails || []).forEach((cargo) => {
            if (cargo.containerNo) containers.add(String(cargo.containerNo).replace(/[^A-Z0-9]/gi, "").toUpperCase());
            (cargo.containers || []).forEach(
                (c) => c.containerNo && containers.add(String(c.containerNo).replace(/[^A-Z0-9]/gi, "").toUpperCase())
            );
        });
        const candidates = trackings.filter(
            (t) => String(t.oceanShipment || t.linkedShipmentId || "") === String(shipment.id) || containers.has(t.containerNumber)
        );
        if (!candidates.length) return null;
        return candidates.sort(
            (a, b) =>
                Number(a.trackingStatus === "COMPLETED") - Number(b.trackingStatus === "COMPLETED") ||
                new Date(b.updated_at || 0) - new Date(a.updated_at || 0)
        )[0];
    };

    const renderTrackingBadge = (shipment) => {
        const tracking = trackingFor(shipment);
        if (!tracking) {
            return (
                <MDTypography variant="caption" sx={{ color: "#94a3b8" }}>
                    Not tracked
                </MDTypography>
            );
        }
        const state = trackingRowState(tracking);
        const hint = trackingArrivalHint(tracking);
        return (
            <Tooltip arrow title={`${tracking.containerNumber} · open in Ocean Shipment Status`}>
                <MDBox
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/ocean-shipping-order/shipment-status/details?open=${tracking.id}`);
                    }}
                    sx={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}
                >
                    <TrackingPill tone={state.tone}>{state.label}</TrackingPill>
                    {hint && (
                        <MDTypography variant="caption" sx={{ color: TRACKING_TONE[hint.tone].color, fontWeight: 600, whiteSpace: "nowrap" }}>
                            {tracking.ata ? "ATA" : "ETA"} {new Date(tracking.ata || tracking.eta).toLocaleDateString("en-GB", { timeZone: "Europe/Istanbul", day: "2-digit", month: "short" })} · {hint.text}
                        </MDTypography>
                    )}
                </MDBox>
            </Tooltip>
        );
    };

    const mergePlannedRouteCountries = (rows) => {
        const allCountries = rows.flatMap(
            (shipment) => shipment.attributes?.carbonFootprint?.plannedRouteCountries || []
        );

        return [...new Set(allCountries)];
    };

    const clickAddHandler = () => {
        navigate("/shipping-order/create-ocean-shipment/details");
    };

    const clickEditHandler = (id, shipment = null) => {
        if (shipment && isDirectBLShipment(shipment)) {
            navigate(`/shipping-order/create-ocean-shipment/details?id=${id}&openDmbl=1`);
            return;
        }

        navigate(`/shipping-order/create-ocean-shipment/details?id=${id}`);
    };

    // A linked HBL leaves its OMBL behind: if it was the OMBL's only HBL the user decides
    // whether the OMBL goes too or stays for another HBL; with other HBLs it is only unlinked.
    const askOmblActionOnDelete = async (shipment) => {
        const linked = shipment?.attributes?.linkedOmbl;
        if (!linked) {
            return (await appConfirm("Are you sure you want to delete this ocean shipment?")) ? "none" : null;
        }

        const omblNo = linked.omblNumber || "its OMBL";
        const otherCount = Math.max((linked.shipmentCount || 1) - 1, 0);

        if (otherCount > 0) {
            return (await appConfirm(
                `This HBL is connected to ${omblNo}, which has ${otherCount} other HBL${otherCount > 1 ? "s" : ""}.\n` +
                    `The HBL will be deleted and removed from ${omblNo}; ${omblNo} stays with its other HBLs.`
            )) ? "keep" : null;
        }

        return appChoose({
            title: "Delete connected HBL?",
            tone: "warning",
            message:
                `This HBL is the only shipment on ${omblNo}.\n\n` +
                `Delete ${omblNo} as well, or keep it so another HBL can be connected to it?`,
            choices: [
                { label: `Keep ${omblNo}`, value: "keep", tone: "info" },
                { label: `Delete HBL + ${omblNo}`, value: "delete", tone: "danger" },
            ],
        });
    };

    const clickDeleteHandler = async (_e, id) => {
        try {
            const shipment = data.find((row) => String(row.id) === String(id));
            const omblAction = await askOmblActionOnDelete(shipment);
            if (!omblAction) return;

            await CrudService.deleteOceanShipment(id, omblAction === "delete" ? "delete" : undefined);
            await fetchOceanShipments();

            const omblNo = shipment?.attributes?.linkedOmbl?.omblNumber;
            if (omblAction === "delete") showNotification(`Ocean shipment and ${omblNo} deleted successfully`);
            else if (omblAction === "keep") showNotification(`Ocean shipment deleted; ${omblNo} kept for another HBL`);
            else showNotification("Ocean shipment deleted successfully");
        } catch (error) {
            console.error("Error deleting ocean shipment:", error);
            showNotification("Failed to delete ocean shipment");
        }
    };

    // An HBL already on an OMBL (backend sends it as `linkedOmbl`) cannot be connected again.
    const linkedOmblMessage = (shipment) => {
        const linked = shipment?.attributes?.linkedOmbl;
        return linked ? `This HBL is already connected to ${linked.omblNumber || "an OMBL"}.` : "";
    };

    const connectBlockedMessage = (rows) =>
        rows.map(linkedOmblMessage).find(Boolean) || "";

    // Shipments share an OMBL only with the same POL and POD.
    const checkSameRoute = (selectedRows, shipment) => {
        const conflict = selectedRows.find(
            (row) => !sameRoute(row.attributes, shipment.attributes)
        );

        if (!conflict) return true;

        showNotification(
            `Different POL/POD: OHFL${shipment.attributes?.shipmentNo || ""} (${describeRoute(
                shipment.attributes
            )}) cannot be added with the selected shipments (${describeRoute(
                conflict.attributes
            )}). Only shipments with the same POL and POD can share an OMBL.`
        );

        return false;
    };

    const handleAddRowLCL = (shipment) => {
        if (isDirectBLShipment(shipment)) {
            showNotification("DBL shipments cannot be connected to OMBL.");
            return;
        }

        if (linkedOmblMessage(shipment)) {
            showNotification(linkedOmblMessage(shipment));
            return;
        }

        if (!checkSameRoute(selectedRowsLCL, shipment)) return;

        const shipmentTotals = shipment.attributes?.totals || {};

        const currentTotalCbm = selectedRowsLCL.reduce(
            (sum, s) => sum + Number(s.attributes?.totals?.totalCbm || 0),
            0
        );

        const newCBM = currentTotalCbm + Number(shipmentTotals.totalCbm || 0);

        if (newCBM > MAX_CBM) {
            showNotification("Cannot add this shipment. It exceeds the maximum limits.");
            return;
        }

        if (selectedRowsLCL.some((r) => String(r.id) === String(shipment.id))) return;

        const next = [...selectedRowsLCL, shipment];

        setSelectedRowsLCL(next);
        setSelectedPlannedRouteCountries(mergePlannedRouteCountries(next));
    };

    const handleRemoveRowLCL = (shipment) => {
        const next = selectedRowsLCL.filter((r) => String(r.id) !== String(shipment.id));

        setSelectedRowsLCL(next);
        setSelectedPlannedRouteCountries(mergePlannedRouteCountries(next));
    };

    const handleAddRowFCL = (shipment) => {
        if (isDirectBLShipment(shipment)) {
            showNotification("DBL shipments cannot be connected to OMBL.");
            return;
        }

        if (linkedOmblMessage(shipment)) {
            showNotification(linkedOmblMessage(shipment));
            return;
        }

        if (selectedRowsFCL.some((r) => String(r.id) === String(shipment.id))) return;

        if (!checkSameRoute(selectedRowsFCL, shipment)) return;

        setSelectedRowsFCL((prev) => [...prev, shipment]);
    };

    const handleRemoveRowFCL = (shipment) => {
        setSelectedRowsFCL((prev) =>
            prev.filter((r) => String(r.id) !== String(shipment.id))
        );
    };

    const handleConnectMBLFromSelectedLCL = () => {
        const hblRows = selectedRowsLCLMemo.filter((s) => !isDirectBLShipment(s));

        if (!hblRows.length) {
            showNotification("DBL shipments cannot be connected to OMBL.");
            return;
        }

        navigate("/ocean-shipping-order/connect-ombl", {
            state: { selectedShipments: hblRows },
        });
    };

    const handleConnectMBLFromSelectedFCL = () => {
        const hblRows = selectedRowsFCLMemo.filter((s) => !isDirectBLShipment(s));

        if (!hblRows.length) {
            showNotification("DBL shipments cannot be connected to OMBL.");
            return;
        }

        navigate("/ocean-shipping-order/connect-ombl", {
            state: { selectedShipments: hblRows },
        });
    };

    // POL / POD of the shipment as "Port, Country" (the vessel route entered on
    // the shipment form); "-" when it was not filled in.
    const renderPortCell = (place) => {
        const port = String(place?.port || "").trim();
        const country = String(place?.country || "").trim();
        const text = [port, country].filter(Boolean).join(", ");

        if (!text) return <span style={{ opacity: 0.5 }}>-</span>;

        return (
            <Tooltip title={text} arrow>
                <span
                    style={{
                        display: "inline-block",
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {text}
                </span>
            </Tooltip>
        );
    };

    const getRows = (dataList) => {
        const fclRows = [];
        const lclRows = [];

        (dataList || []).forEach((shipment) => {
            const attr = shipment.attributes || {};
            const shipmentTotals = attr.totals || {};

            const finalRate = attr.finalSellingRate ?? attr.sellingRate ?? 0;
            const sellingRateCurrency = formatMoney(finalRate, attr.sellingRateCurrency);

            const transportType = attr.oceanTransportType || "";
            const isLCL = transportType.includes("LCL");
            const isFCL = transportType.includes("FCL");

            const blType = getBlType(shipment);
            const isDBL = isDirectBLShipment(shipment);

            const linkedMessage = linkedOmblMessage(shipment);

            const alreadySelectedLCL = selectedRowsLCL.some(
                (r) => String(r.id) === String(shipment.id)
            );

            const alreadySelectedFCL = selectedRowsFCL.some(
                (r) => String(r.id) === String(shipment.id)
            );

            const rowData = {
                id: shipment.id,

                shipmentNo: (
                    <ListLink onClick={() => clickEditHandler(shipment.id, shipment)}>
                        {`OHFL${attr.shipmentNo || 0}`}
                    </ListLink>
                ),

                customer: (
                    <Tooltip title={renderText(attr.customer, "No Customer Data")} arrow>
            <span
                style={{
                    display: "inline-block",
                    maxWidth: 230,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#101828",
                }}
            >
              {getCustomerText(shipment)}
            </span>
                    </Tooltip>
                ),

                billOfLadingType: renderBlTypeChip(blType),
                oceanTransportType: renderTransportChip(transportType),
                pol: renderPortCell(attr.vesselPol),
                pod: renderPortCell(attr.vesselPod),
                tracking: renderTrackingBadge(shipment),
                totalVolume: renderVolumeChip(shipmentTotals.totalCbm),
                sellingRate: renderMoneyChip(sellingRateCurrency),

                actions: (
                    <CrmRowActions onEdit={() => clickEditHandler(shipment.id, shipment)} editTitle="Edit Ocean Shipment" onDelete={(e) => clickDeleteHandler(e, shipment.id)} deleteTitle="Delete Ocean Shipment">{!isDBL && isFCL && <ListIconButton title={linkedMessage || (alreadySelectedFCL ? "Already Selected" : "Add Shipment")} onClick={() => handleAddRowFCL(shipment)} disabled={alreadySelectedFCL || Boolean(linkedMessage)} tone="green"><AddIcon /></ListIconButton>}{!isDBL && isLCL && <ListIconButton title={linkedMessage || (alreadySelectedLCL ? "Already Selected" : "Add Shipment")} onClick={() => handleAddRowLCL(shipment)} disabled={alreadySelectedLCL || Boolean(linkedMessage)} tone="green"><AddIcon /></ListIconButton>}{isDBL && <ListIconButton title="DBL shipments cannot be connected to OMBL" disabled={true} tone="green"><AddIcon /></ListIconButton>}</CrmRowActions>
                ),
            };

            if (isFCL) fclRows.push(rowData);
            if (isLCL) lclRows.push(rowData);
        });

        return { fcl: fclRows, lcl: lclRows };
    };

    useEffect(() => {
        setTableData(getRows(data));
    }, [data, selectedRowsLCL, selectedRowsFCL, trackings]);

    const selectedTableColumns = [
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Shipment No</span>
            ),
            accessor: "shipmentNo",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Customer</span>
            ),
            accessor: "customer",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>BL Type</span>
            ),
            accessor: "billOfLadingType",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>
          Ocean Transport Type
        </span>
            ),
            accessor: "oceanTransportType",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>POL</span>
            ),
            accessor: "pol",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>POD</span>
            ),
            accessor: "pod",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Selling Rate</span>
            ),
            accessor: "sellingRateCurrency",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Actions</span>
            ),
            accessor: "actions",
            disableSortBy: true,
        },
    ];

    const getSelectedRowsTable = (selectedData, type) =>
        (selectedData || []).map((shipment) => {
            const attr = shipment.attributes || {};
            const shipmentTotals = attr.totals || {};

            const finalRate = attr.finalSellingRate ?? attr.sellingRate ?? 0;
            const sellingRateCurrency = formatMoney(finalRate, attr.sellingRateCurrency);

            return {
                id: shipment.id,

                shipmentNo: (
                    <MDTypography
                        variant="button"
                        fontWeight="bold"
                        color="info"
                        sx={{ whiteSpace: "nowrap" }}
                    >
                        {`OHFL${attr.shipmentNo || 0}`}
                    </MDTypography>
                ),

                customer: (
                    <Tooltip title={renderText(attr.customer, "Unknown Customer")} arrow>
            <span
                style={{
                    display: "inline-block",
                    maxWidth: 230,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#101828",
                }}
            >
              {getCustomerText(shipment)}
            </span>
                    </Tooltip>
                ),

                billOfLadingType: renderBlTypeChip(getBlType(shipment)),
                oceanTransportType: renderTransportChip(attr.oceanTransportType || ""),
                pol: renderPortCell(attr.vesselPol),
                pod: renderPortCell(attr.vesselPod),
                totalVolume: renderVolumeChip(shipmentTotals.totalCbm),
                sellingRateCurrency: renderMoneyChip(sellingRateCurrency),

                actions: (
                    <MDBox display="flex" alignItems="center" gap={0.5}>
                        <Tooltip title="Remove Shipment">
                            <RemoveRowButton onClick={() =>
                                    type === "FCL"
                                        ? handleRemoveRowFCL(shipment)
                                        : handleRemoveRowLCL(shipment)
                                } tooltip="Remove row fcl" />
                        </Tooltip>
                    </MDBox>
                ),
            };
        });

    const selectedDataTableDataFCL = {
        columns: selectedTableColumns,
        rows: getSelectedRowsTable(selectedRowsFCLMemo, "FCL"),
    };

    const selectedDataTableDataLCL = {
        columns: selectedTableColumns,
        rows: getSelectedRowsTable(selectedRowsLCLMemo, "LCL"),
    };

    const dataTableColumns = [
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Shipment No</span>
            ),
            accessor: "shipmentNo",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Customer</span>
            ),
            accessor: "customer",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>BL Type</span>
            ),
            accessor: "billOfLadingType",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>
          Ocean Transport Type
        </span>
            ),
            accessor: "oceanTransportType",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>POL</span>
            ),
            accessor: "pol",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>POD</span>
            ),
            accessor: "pod",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Tracking</span>
            ),
            accessor: "tracking",
            disableSortBy: true,
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Selling Rate</span>
            ),
            accessor: "sellingRate",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Actions</span>
            ),
            accessor: "actions",
            disableSortBy: true,
        },
    ];

    const handleTabChange = (_event, newValue) => {
        setTabValue(newValue);
    };

    const EmptyState = ({ label }) => (
        <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={6}
            gap={1}
        >
            <InboxIcon sx={{ fontSize: 42, color: "text.disabled" }} />
            <MDTypography variant="button" color="secondary" fontWeight="regular">
                {label}
            </MDTypography>
        </MDBox>
    );

    const CapacityGauge = ({ label, value, max, unit, color }) => {
        const pct = Math.min(100, Math.round((value / max) * 100));
        return (
            <MDBox flex={1} minWidth={180}>
                <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                    <MDTypography variant="caption" fontWeight="medium" color="text">
                        {label}
                    </MDTypography>
                    <MDTypography variant="caption" fontWeight="bold" color={color}>
                        {value.toFixed(2)} / {max} {unit}
                    </MDTypography>
                </MDBox>
                <MDProgress
                    variant="gradient"
                    color={pct >= 100 ? "error" : pct >= 80 ? "warning" : color}
                    value={pct}
                />
            </MDBox>
        );
    };

    const tableWrapperSx = {
        p: 2,
        borderRadius: "18px",
        background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: "1px solid #e5e7eb",
        boxShadow: "0 12px 32px rgba(15,23,42,0.08)",
        overflow: "hidden",
    };

    return (
        <DashboardLayout>
          <DashboardNavbar />
          <CrmPage>
            <CrmPageHeader
                title="Ocean Shipments"
                subtitle={`${tableData.fcl.length} FCL · ${tableData.lcl.length} LCL shipments (HBL and DBL). Open a shipment number to edit it, or add shipments to an OMBL.`}
                actions={
                  <CrmActionButton icon={<AddIcon />} onClick={clickAddHandler}>
                    New Ocean Shipment
                  </CrmActionButton>
                }
            />

            {notification.value && <CrmNotice tone="blue">{notification.text}</CrmNotice>}

            <LoadTypeTabs

                options={[

                    { value: 0, label: "FCL", caption: "Full container load", count: tableData.fcl.length, icon: <DirectionsBoatOutlined /> },

                    { value: 1, label: "LCL", caption: "Less than container load", count: tableData.lcl.length, icon: <Inventory2Outlined /> },

                ]}

                value={tabValue}

                onChange={(value) => setTabValue(value)}

            />

            <CrmTableCard
                title={tabValue === 0 ? "FCL shipments" : "LCL shipments"}
                summary={`${tabValue === 0 ? tableData.fcl.length : tableData.lcl.length} shipments`}
            >
              {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={8}>
                    <CircularProgress size={32} />
                  </MDBox>
              ) : (
                  <>
                    {tabValue === 0 && (
                        tableData.fcl.length > 0 ? (
                            <DataTable table={{ columns: dataTableColumns, rows: tableData.fcl }} canSearch />
                        ) : (
                            <EmptyState label="No FCL shipments yet." />
                        )
                    )}
                    {tabValue === 1 && (
                        tableData.lcl.length > 0 ? (
                            <DataTable table={{ columns: dataTableColumns, rows: tableData.lcl }} canSearch />
                        ) : (
                            <EmptyState label="No LCL shipments yet." />
                        )
                    )}
                  </>
              )}
            </CrmTableCard>

            {tabValue === 0 && selectedRowsFCLMemo.length > 0 && (
                <CrmTableCard title="Selected shipments (FCL)" summary={`${selectedRowsFCLMemo.length} selected · ready to connect to an OMBL`}>
                  <DataTable table={selectedDataTableDataFCL} canSearch />
                  <MDBox display="flex" justifyContent="flex-end" p={2.5} pt={0}>
                    <CrmActionButton
                        icon={<LinkIcon />}
                        onClick={handleConnectMBLFromSelectedFCL}
                        disabled={Boolean(connectBlockedMessage(selectedRowsFCLMemo))}
                        title={connectBlockedMessage(selectedRowsFCLMemo) || undefined}
                    >
                      Connect OMBL
                    </CrmActionButton>
                  </MDBox>
                </CrmTableCard>
            )}

            {tabValue === 1 && selectedRowsLCLMemo.length > 0 && (
                <CrmTableCard title="Selected shipments (LCL)" summary={`${selectedRowsLCLMemo.length} selected · check the capacity before connecting`}>
                  <MDBox display="flex" flexWrap="wrap" gap={3} px={2.5} pt={2}>
                    <CapacityGauge
                        label="Total CBM"
                        value={selectedTotalCbmLCL}
                        max={MAX_CBM}
                        unit="m³"
                        color="info"
                    />
                  </MDBox>
                  <DataTable table={selectedDataTableDataLCL} canSearch />
                  <MDBox display="flex" justifyContent="flex-end" p={2.5} pt={0}>
                    <CrmActionButton
                        icon={<LinkIcon />}
                        onClick={handleConnectMBLFromSelectedLCL}
                        disabled={Boolean(connectBlockedMessage(selectedRowsLCLMemo))}
                        title={connectBlockedMessage(selectedRowsLCLMemo) || undefined}
                    >
                      Connect OMBL
                    </CrmActionButton>
                  </MDBox>
                </CrmTableCard>
            )}
          </CrmPage>

          <Footer />
        </DashboardLayout>
    );
}

export default OceanShipmentTable;